import { Center, Loader, Text, Title } from '@cowbell/ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { useCustomerSpotlightById } from '../customer-spotlights/spotlight.hooks';
import { usePersona } from '../../components/hooks/usePersona';
import useNotifications from '../../_lib/notifications/useNotifications';
import { SpotlightAffectedProductsTable } from './spotlight-details/SpotlightAffectedProductsTable';
import {
  SpotlightDetailsLoadErrorAlert,
  SpotlightDetailsMissingIdAlert,
  SpotlightDetailsNotFoundAlert,
  SpotlightDetailsPatchLinkAlert,
} from './spotlight-details/SpotlightDetailsAlerts';
import { SpotlightDetailsBreadcrumbs } from './spotlight-details/SpotlightDetailsBreadcrumbs';
import { SpotlightDetailsMetadataCard } from './spotlight-details/SpotlightDetailsMetadataCard';
import type {
  SpotlightDetailsPersonaForNavigation,
  SpotlightProduct,
} from './spotlight-details/spotlightDetails.types';
import { useSpotlightProductTable } from './spotlight-details/useSpotlightProductTable';
import classes from './_.module.css';

const SpotlightDetails = () => {
  const routeParams = useParams<{ spotlightId: string }>();
  const spotlightId = routeParams.spotlightId;
  const queryClient = useQueryClient();
  const persona = usePersona() as SpotlightDetailsPersonaForNavigation;
  const notifications = useNotifications();

  const { query: spotlightQuery, queryKey } =
    useCustomerSpotlightById(spotlightId);

  const productTable = useSpotlightProductTable({
    spotlightId,
    spotlight: spotlightQuery.data,
    queryClient,
    notifications,
    queryKey,
  });

  if (!spotlightId) {
    return <SpotlightDetailsMissingIdAlert persona={persona} />;
  }

  if (spotlightQuery.isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (spotlightQuery.isError) {
    return (
      <SpotlightDetailsLoadErrorAlert
        onRetry={() => spotlightQuery.refetch()}
      />
    );
  }

  if (!spotlightQuery.data) {
    return <SpotlightDetailsNotFoundAlert />;
  }

  const spotlight = spotlightQuery.data;

  const products: SpotlightProduct[] = Array.isArray(spotlight.products)
    ? spotlight.products
    : [];
  const vendorAdvisoryLinks = spotlight.vendorAdvisoryLinks ?? [];

  return (
    <div className={classes.spotlightDetails}>
      <SpotlightDetailsBreadcrumbs
        persona={persona}
        accountId={spotlight.accountId}
      />
      <Title order={2} mt="md" mb="xs" className={classes.title} fw={700}>
        {spotlight.reasonDescription}
      </Title>
      {Array.isArray(spotlight.cve) && (
        <Text className={classes.cve} size="md" mb="md">
          CVE(s): {spotlight.cve.join(', ')}
        </Text>
      )}
      {vendorAdvisoryLinks.length > 0 && (
        <SpotlightDetailsPatchLinkAlert
          vendorAdvisoryLinks={vendorAdvisoryLinks}
        />
      )}
      <SpotlightDetailsMetadataCard
        dataSource={spotlight.dataSource}
        mostImpactedCF={spotlight.mostImpactedCF}
        importance={spotlight.importance}
        created={spotlight.created}
        status={spotlight.status}
      />
      <SpotlightAffectedProductsTable
        products={products}
        rowStates={productTable.rowStates}
        onVersionChange={productTable.handleVersionChange}
        onPatch={productTable.handlePatch}
        onNotApplicable={productTable.handleNotApplicable}
        onUndo={productTable.handleUndo}
        onSubmitForReview={productTable.handleSubmitForReview}
        handleContactCyberExpert={productTable.handleContactCyberExpert}
        onDecline={productTable.handleDecline}
        onSendEmailToDeclineProducts={
          productTable.handleSendEmailToDeclineProducts
        }
        spotlightStatus={spotlight.status}
      />
    </div>
  );
};

export default SpotlightDetails;
